import { z } from 'zod';
import { Tool } from './Tool';
import { getConsoleMessages, groupErrors, getErrorSummary, parseError } from '@/lib/consoleMessages';

export interface ReadConsoleMessagesParams {
  filter?: 'error' | 'warn' | 'info' | 'debug' | 'log' | 'all';
  limit?: number;
  format?: 'raw' | 'parsed';
}

/**
 * AI tool for reading console messages from the project preview.
 * This tool allows AI assistants to access console logs for debugging assistance.
 */
export class ReadConsoleMessagesTool implements Tool<ReadConsoleMessagesParams> {
  readonly description = 'Read console messages from the project preview. Can filter by level and limit results.';

  readonly inputSchema = z.object({
    filter: z.enum(['error', 'warn', 'info', 'debug', 'log', 'all']).optional().describe(
      'Filter messages by level. Defaults to "all" to show all message types.'
    ),
    limit: z.number().min(1).max(200).optional().describe(
      'Maximum number of most recent messages to return. Defaults to 50. Use smaller values (10-20) for quick checks, larger values (100-200) for comprehensive debugging.'
    ),
    format: z.enum(['raw', 'parsed']).optional().describe(
      'Output format. "raw" shows original messages, "parsed" provides categorized errors with explanations. Defaults to "parsed".'
    ),
  });

  async execute(params: ReadConsoleMessagesParams): Promise<string> {
    const { filter = 'all', limit = 50, format = 'parsed' } = params;

    let messages = getConsoleMessages()
      .filter(msg => filter === 'all' || msg.level === filter);

    // Always apply a limit to prevent unbounded output
    messages = messages.slice(-limit);

    if (messages.length === 0) {
      return `No console messages found${filter !== 'all' ? ` for level: ${filter}` : ''}.`;
    }

    const suffix = filter !== 'all' ? ` (level: ${filter})` : '';
    const totalAvailable = getConsoleMessages().filter(msg => filter === 'all' || msg.level === filter).length;
    const truncatedNote = totalAvailable > messages.length ? ` (showing last ${messages.length} of ${totalAvailable})` : '';

    // Raw format - original behavior
    if (format === 'raw') {
      const formatted = messages
        .map(msg => `[${msg.level.toUpperCase()}] ${msg.message}`)
        .join('\n');

      return `Found ${messages.length} console message${messages.length !== 1 ? 's' : ''}${suffix}${truncatedNote}:\n\n${formatted}`;
    }

    // Parsed format - grouped and categorized errors
    const summary = getErrorSummary(messages);
    const grouped = groupErrors(messages);

    let output = `## Console Messages Summary${suffix}${truncatedNote}\n\n`;
    
    // Add summary statistics
    output += `**Total:** ${summary.total} messages\n`;
    if (summary.critical > 0) output += `**Critical Errors:** ${summary.critical}\n`;
    if (summary.warnings > 0) output += `**Warnings:** ${summary.warnings}\n`;
    if (summary.info > 0) output += `**Info:** ${summary.info}\n`;
    
    // Category breakdown
    const categories = Object.entries(summary.categories);
    if (categories.length > 0) {
      output += `\n**By Category:**\n`;
      for (const [category, count] of categories) {
        output += `- ${category}: ${count}\n`;
      }
    }
    
    output += '\n---\n\n';

    // Detailed grouped errors
    for (const group of grouped) {
      const firstError = group.errors[0];
      const { category, plainEnglish, technicalDetails, possibleCause, suggestedFix } = firstError;
      
      output += `### ${category.icon} ${category.name}`;
      if (group.count > 1) {
        output += ` (×${group.count})`;
      }
      output += '\n\n';
      
      output += `**What happened:** ${plainEnglish}\n\n`;
      output += `**Possible cause:** ${possibleCause}\n\n`;
      output += `**Suggested fix:** ${suggestedFix}\n\n`;
      output += `<details>\n<summary>Technical details</summary>\n\n\`\`\`\n${technicalDetails}\n\`\`\`\n</details>\n\n`;
      
      // If there are multiple occurrences, show a few examples
      if (group.count > 1 && group.errors.length > 1) {
        const additionalExamples = group.errors.slice(1, 3);
        if (additionalExamples.length > 0) {
          output += `<details>\n<summary>Additional occurrences (${group.count - 1} more)</summary>\n\n`;
          for (const error of additionalExamples) {
            output += `\`\`\`\n${error.technicalDetails}\n\`\`\`\n\n`;
          }
          if (group.count > 3) {
            output += `...and ${group.count - 3} more similar messages\n`;
          }
          output += `</details>\n\n`;
        }
      }
      
      output += '---\n\n';
    }

    return output.trim();
  }
}
